import { useState, useEffect } from 'react';
import {
  loginWithEmail,
  registerWithEmail,
  loginWithMicrosoft,
  loginWithGoogle,
  logout as logoutService,
  subscribeToAuthChanges,
} from '../firebase/auth';
import { subscribeToAuthSettings, DEFAULT_AUTH_SETTINGS, isEmailDomainAllowed } from '../firebase/authSettings';

class DomainNotAllowedError extends Error {
  constructor(domain) {
    super(`Domena "${domain}" nie jest dozwolona w tym systemie.`);
    this.code = 'auth/domain-not-allowed';
    this.name = 'DomainNotAllowedError';
  }
}

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(DEFAULT_AUTH_SETTINGS);

  useEffect(() => {
    const unsubAuth = subscribeToAuthChanges((firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });
    const unsubSettings = subscribeToAuthSettings((s) => setSettings(s));
    return () => {
      unsubAuth();
      unsubSettings();
    };
  }, []);

  const enforceDomain = async (cred) => {
    const email = cred?.user?.email;
    if (!isEmailDomainAllowed(email, settings.allowedEmailDomains)) {
      const domain = email ? email.slice(email.lastIndexOf('@') + 1) : '(brak emaila)';
      await logoutService();
      throw new DomainNotAllowedError(domain);
    }
    return cred;
  };

  const login = async (email, password) => {
    const cred = await loginWithEmail(email, password);
    return enforceDomain(cred);
  };

  const register = async (email, password) => {
    const cred = await registerWithEmail(email, password);
    return enforceDomain(cred);
  };

  const loginMicrosoftFn = async () => {
    const cred = await loginWithMicrosoft(settings.microsoftTenantId);
    return enforceDomain(cred);
  };

  const loginGoogleFn = async () => {
    const cred = await loginWithGoogle();
    return enforceDomain(cred);
  };

  const logout = async () => {
    return logoutService();
  };

  return {
    user,
    loading,
    settings,
    login,
    register,
    loginMicrosoft: loginMicrosoftFn,
    loginGoogle: loginGoogleFn,
    logout,
  };
}
